import React, { useState } from 'react';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Typography,
  Avatar,
  ButtonGroup,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SpaIcon from '@mui/icons-material/Spa';
import { StatusMember, DeleteJoinMember } from "../Apihelper/MemberApiHelpr";
import { CreateSteambath, UseSteambath } from "../Apihelper/SteambathHelper";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PayButton from "../../components/PayButton/PayButton";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: '#f8fafc',
  color: theme.palette.primary.main,
  borderBottom: '2px solid #e2e8f0',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f8fafc',
  },
  '&:hover': {
    backgroundColor: '#f1f5f9',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
  padding: '6px 16px',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

const SteambathBadge = styled(Box)(({ available }) => ({
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: available === "Yes" ? '#dcfce7' : '#fee2e2',
  color: available === "Yes" ? '#059669' : '#dc2626',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
}));

const SteambathCount = styled(Box)({
  fontSize: '0.75rem',
  fontWeight: 500,
  backgroundColor: 'rgba(0,0,0,0.1)',
  padding: '2px 6px',
  borderRadius: '12px',
  marginLeft: '4px'
});

function MemberTableView({ members, onLoadMore, hasMore, onStatusChange, onDelete, isLoading }) {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [openAvatarPopup, setOpenAvatarPopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  // Add steambath states
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [steambath, setOpensteambath] = useState(false);
  const [steambathvalue, setsteambathValue] = useState('');
  const [steambathquantity, setSteambathQuantity] = useState("");
  const [steambathPaymentDate, setSteambathPaymentDate] = useState(null);
  const parsePaymentDateToDayjs = (paymentDate) => {
    if (paymentDate == null || paymentDate === "") return dayjs();
    const s = String(paymentDate).trim();
    const parts = s.split(/[-/]/);
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (!isNaN(d) && !isNaN(m) && !isNaN(y) && y > 31) {
        return dayjs(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
      }
    }
    const parsed = dayjs(s);
    return parsed.isValid() ? parsed : dayjs();
  };

  const handleDeleteClick = (member) => {
    setSelectedMember(member);
    setOpenDeleteDialog(true);
  };

  const handleStatusClick = (member) => {
    setSelectedMember(member);
    setIsActive(member.status === 1);
    setOpenStatusDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;

      const res = await DeleteJoinMember(userToken, userId, selectedMember.id);

      if (res.code === "200") {
        onDelete();
        setOpenDeleteDialog(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleConfirmStatus = async () => {
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const newStatus = selectedMember.status === 1 ? 0 : 1;

      const res = await StatusMember(userToken, userId, newStatus, selectedMember.id);

      if (res.code == "200") {
        onStatusChange();
        setOpenStatusDialog(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewDetails = (memberId) => {
    navigate('/memberdetails', {
      state: {
        id: memberId,
        random_no: Math.random(),
      },
    });
  };

  const handleAvatarClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setOpenAvatarPopup(true);
  };

  const handleCloseAvatarPopup = () => {
    setOpenAvatarPopup(false);
    setSelectedImage('');
  };

  // Add steambath handlers
  const handleOpensteambath = (memberId, paymentDate) => {
    setSelectedMemberId(memberId);
    setSteambathPaymentDate(parsePaymentDateToDayjs(paymentDate));
    setOpensteambath(true);
  };

  const handleClosesteambath = () => {
    setSteambathQuantity("");
    setsteambathValue("");
    setSteambathPaymentDate(null);
    setSelectedMemberId(null);
    setOpensteambath(false);
  };

  const handleChangeSteambathQuantity = (event) => {
    const inputValue = event.target.value;
    if (/^\d*$/.test(inputValue)) {
      setSteambathQuantity(inputValue);
    }
  };

  const handleChangeSteambathValue = (event) => {
    const inputValue = event.target.value;
    if (/^\d*$/.test(inputValue)) {
      setsteambathValue(inputValue);
    }
  };

  const handleSubmitSteambath = async () => {
    if (!selectedMemberId) {
      console.error("No member ID selected");
      return;
    }

    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const UseradminData = {
        member_id: selectedMemberId,
        quantity: steambathquantity,
        amount: steambathvalue,
        payment_date: steambathPaymentDate?.isValid()
          ? steambathPaymentDate.format("DD-MM-YYYY")
          : dayjs().format("DD-MM-YYYY"),
      };

      const response = await CreateSteambath(userToken, UseradminData);
      
      if (response.code === 200) {
        handleClosesteambath();
        onStatusChange(); // Refresh the member list
      } else {
        console.error("Failed to add steam bath:", response.message);
      }
    } catch (error) {
      console.error("API Error:", error.message);
    }
  };

  const handleUseBath = async (id) => {
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const UseradminData = {
        member_id: id,
      };

      const response = await UseSteambath(userToken, UseradminData);
      
      if (response.code === 200) {
        onStatusChange(); // Refresh the member list
      } else {
        console.error("Failed to use steam bath:", response.message);
      }
    } catch (error) {
      console.error("API Error:", error.message);
    }
  };

  return (
    <>
    
      <TableContainer 
        component={Paper} 
        sx={{
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          borderRadius: "10px",
          overflow: "hidden",
          margin: "20px 0",
        }}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', margin: '50px auto' }}>
            <CircularProgress sx={{ color: appColors.evergreen }} />
          </div>
        ) : (
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Membership Id</StyledTableCell>
                <StyledTableCell align="left">Name</StyledTableCell>
                <StyledTableCell align="left">Phone No.</StyledTableCell>
                <StyledTableCell align="left">Email</StyledTableCell>
                <StyledTableCell align="left">Status</StyledTableCell>
             
                <StyledTableCell align="left">Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((row) => (
                <StyledTableRow key={row.id}>
                  <TableCell onClick={() => handleViewDetails(row.id)}>
                    <Typography sx={{
                      color: appColors.evergreen,
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}>
                      {row.membership_number}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={row.image} 
                        alt={row.name} 
                        sx={{ 
                          marginRight: '10px', 
                          cursor: 'pointer',
                          border: `2px solid ${appColors.evergreen}` 
                        }}
                        onClick={() => handleAvatarClick(row.image)}
                      />
                      {row.name}
                    </div>
                  </TableCell>
                  <TableCell align="left">{row.phone}</TableCell>
                  <TableCell align="left">{row.email}</TableCell>
                  <TableCell align="left">
                    <Button
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'none',
                        borderColor: row.status === 1 ? '#059669' : '#dc2626',
                        color: row.status === 1 ? '#059669' : '#dc2626',
                        '&:hover': {
                          borderColor: row.status === 1 ? '#047857' : '#b91c1c',
                          backgroundColor: 'transparent'
                        }
                      }}
                      onClick={() => handleStatusClick(row)}
                    >
                      {row.status === 1 ? 'Active' : 'Inactive'}
                    </Button>
                  </TableCell>
                 
                  <TableCell align="center">
                    <ButtonGroup variant="outlined" size="small">
                      <Tooltip title="View Details">
                        <ActionButton
                          onClick={() => handleViewDetails(row.id)}
                          sx={{ 
                            color: appColors.evergreen, 
                            borderColor: appColors.evergreen,
                            '&:hover': {
                              backgroundColor: 'rgba(31, 122, 84, 0.04)'
                            }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title={row.bath_available === "Yes" ? "Use Bath" : "Add Steam Bath"}>
                        <ActionButton
                          onClick={() =>
                            row.bath_available === "Yes"
                              ? handleUseBath(row.id)
                              : handleOpensteambath(
                                  row.id,
                                  row.bath_payment_date ?? row.payment_date ?? row.steam_bath_payment_date
                                )
                          }
                          sx={{
                            color: appColors.evergreen,
                            borderColor: appColors.evergreen,
                            '&:hover': {
                              backgroundColor: 'rgba(31, 122, 84, 0.04)'
                            }
                          }}
                        >
                          {row.bath_available === "Yes" ? <AddIcon fontSize="small" /> : <SpaIcon fontSize="small" />}
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <ActionButton
                          onClick={() => handleDeleteClick(row)}
                          sx={{ 
                            color: '#dc2626', 
                            borderColor: '#dc2626',
                            '&:hover': {
                              backgroundColor: 'rgba(220, 38, 38, 0.04)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    </ButtonGroup>
                    {row.bath_available === "Yes" && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    mt: 1,
                                    color: '#059669',
                                    fontWeight: 600
                                  }}
                                >
                                  Remaining Baths: {row.remaining_bath}
                                </Typography>
                                  )}
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Box 
        sx={{ 
          textAlign: "center", 
          marginTop: "20px",
          padding: { xs: '0 16px', sm: 0 }
        }}
      >
        <Button
          variant="contained"
          onClick={onLoadMore}
          disabled={!hasMore || isLoading}
          sx={{
            fontWeight: 'bold',
            textTransform: 'none',
            backgroundColor: appColors.evergreen,
            '&:hover': {
              backgroundColor: appColors.pine
            },
            '&.Mui-disabled': {
              backgroundColor: '#94a3b8'
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : members.length === 0 ? (
            'No Records'
          ) : (
            'Load More'
          )}
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#dc2626', fontWeight: 'bold' }}>
          Delete Member
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this member?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<CancelIcon />}
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ 
              color: '#64748b',
              fontWeight: 'bold',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            startIcon={<DeleteIcon />}
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#dc2626',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#b91c1c'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog 
        open={openStatusDialog} 
        onClose={() => setOpenStatusDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: appColors.evergreen, fontWeight: 'bold' }}>
          {isActive ? 'Deactivate' : 'Activate'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isActive
              ? 'Are you sure you want to deactivate this member?'
              : 'Are you sure you want to activate this member?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<CancelIcon />}
            onClick={() => setOpenStatusDialog(false)}
            sx={{ 
              color: '#64748b',
              fontWeight: 'bold',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            startIcon={<CheckCircleIcon />}
            onClick={handleConfirmStatus}
            variant="contained"
            sx={{
              backgroundColor: appColors.evergreen,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: appColors.pine
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar Popup Dialog */}
      <Dialog 
        open={openAvatarPopup} 
        onClose={handleCloseAvatarPopup}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ padding: 0 }}>
          <img 
            src={selectedImage} 
            alt="avatar" 
            style={{ 
              width: '100%', 
              height: 'auto',
              display: 'block'
            }} 
          />
        </DialogContent>
      </Dialog>

      {/* Add Steambath Dialog */}
      <Dialog 
        open={steambath} 
        onClose={handleClosesteambath}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: appColors.evergreen, fontWeight: 'bold' }}>
          Add Steambath
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Payment date"
              value={steambathPaymentDate}
              onChange={(v) => setSteambathPaymentDate(v)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  margin: "dense",
                  fullWidth: true,
                  sx: { mb: 1 },
                },
              }}
            />
          </LocalizationProvider>
          <TextField
            autoFocus
            margin="dense"
            label="Quantity"
            type="text"
            fullWidth
            variant="outlined"
            value={steambathquantity}
            onChange={handleChangeSteambathQuantity}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: appColors.evergreen,
                },
                '&.Mui-focused fieldset': {
                  borderColor: appColors.evergreen,
                },
              }
            }}
          />
          <TextField
            margin="dense"
            label="Amount"
            type="text"
            fullWidth
            variant="outlined"
            value={steambathvalue}
            onChange={handleChangeSteambathValue}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: appColors.evergreen,
                },
                '&.Mui-focused fieldset': {
                  borderColor: appColors.evergreen,
                },
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClosesteambath}
            sx={{
              color: '#64748b',
              '&:hover': {
                backgroundColor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <PayButton overlay 
            onClick={handleSubmitSteambath}
            variant="contained"
            sx={{
              backgroundColor: appColors.evergreen,
              '&:hover': {
                backgroundColor: appColors.pine
              }
            }}
          >
            Submit
          </PayButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MemberTableView; 